import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useStateRef from '../../enhancers/custom-hook/use-state-ref';
import ListItem, { ListItemDataPropTypes, ListItemPropTypes, SelectionStyles } from './list-item';
import KeyEventValues from '../../../constants/key-event-values';
import useTranslation from '../../../utils/translation-utils';
import './list.scss';

const List = ({
  items,
  onItemClick,
  groupOptionsOnCategory,
  categoryKey,
  renderCustomListItem,
  listItemProps,
}) => {
  const [focusedIndex, setFocusedIndex, focusedIndexRef] = useStateRef(-1);
  const searchTermRef = useRef(null);
  const clearSelectionButtonRef = useRef(null);
  const listItemRefs = useRef([]);
  const [getTranslation] = useTranslation();

  const getFocusableItems = () => {
    const updatedFocusableItems = [];
    if (searchTermRef.current) updatedFocusableItems.push(searchTermRef.current);
    if (listItemRefs.current.length > 0) {
      updatedFocusableItems.push(
        ...listItemRefs.current.filter((dropdownItemRef) => dropdownItemRef),
      );
    }
    if (clearSelectionButtonRef.current) {
      updatedFocusableItems.push(clearSelectionButtonRef.current);
    }
    return updatedFocusableItems;
  };

  const handleKeyDown = (event) => {
    const focusableItems = getFocusableItems();
    const currIndex = focusedIndexRef.current;
    let newIndex = currIndex;
    switch (event.key) {
      case KeyEventValues.ARROW_UP.key:
        event.preventDefault();
        newIndex = (newIndex - 1 + focusableItems.length) % focusableItems.length;
        break;
      case KeyEventValues.ARROW_DOWN.key:
        event.preventDefault();
        newIndex = (newIndex + 1) % focusableItems.length;
        break;
      case KeyEventValues.ENTER.key:
        event.preventDefault();
        focusableItems[newIndex]?.click();
        break;
      default:
        break;
    }
    setFocusedIndex(newIndex);
  };

  const handleItemHighlight = (targetId) => {
    const currIndex = items.findIndex((item) => item.id === targetId);
    if (currIndex !== focusedIndexRef.current) {
      setFocusedIndex(currIndex);
    }
  };

  const handleMouseLeave = () => {
    setFocusedIndex(-1);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      setFocusedIndex(-1);
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  const handleItemClicked = (id, isSelected, selectedObj, event) => {
    setFocusedIndex(getFocusableItems().indexOf(event.currentTarget));
    if (onItemClick && typeof onItemClick === 'function') {
      onItemClick(id, isSelected, selectedObj, event);
    }
  };

  const getEmptyState = () => (
    <span className="ddc-empty-state">{getTranslation('NO_RESULT_FOUND')}</span>
  );

  const getItemsView = () => items.map(
    (
      listItem,
      index,
    ) => {
      const isDifferentCategory = () => items[index][categoryKey]
          && items[index - 1][categoryKey]
          && items[index][categoryKey] !== items[index - 1][categoryKey];

      const setListItemRef = (element) => {
        listItemRefs.current[index] = element;
      };

      return (
        <ListItem
          key={listItem.id}
          data={listItem}
          onClick={handleItemClicked}
          onItemHighlight={handleItemHighlight}
          highlighted={focusedIndex === index}
          ref={setListItemRef}
          showDivider={
              groupOptionsOnCategory && index > 0 && isDifferentCategory()
          }
          renderCustomView={renderCustomListItem}
           // eslint-disable-next-line react/jsx-props-no-spreading
          {...listItemProps}
        />
      );
    },
  );

  return (
    <div className="list">
      {items && (
        <div className="list-items-wrapper" onMouseLeave={handleMouseLeave}>
          {items.length > 0 ? getItemsView() : getEmptyState()}
        </div>
      )}
    </div>
  );
};

List.propTypes = {
  items: PropTypes.oneOf([
    null,
    [],
    PropTypes.arrayOf(PropTypes.shape(ListItemDataPropTypes)),
  ]),
  onItemClick: PropTypes.func,
  groupOptionsOnCategory: PropTypes.bool,
  categoryKey: PropTypes.string,
  renderCustomListItem: PropTypes.oneOf([null, PropTypes.func]),
  listItemProps: PropTypes.shape({ ...ListItemPropTypes }),
};

List.SelectionStyle = SelectionStyles;

List.defaultProps = {
  items: null,
  onItemClick: () => {},
  groupOptionsOnCategory: false,
  categoryKey: 'category',
  renderCustomListItem: null,
  listItemProps: {},
};

export default List;
